/**
 * @description Policy helpers for merchant dashboard transaction polling.
 *
 * Keeps expensive /transactions polling scoped to a visible, authorized tab.
 * Product knob: cadence is owned by the dashboard (30s), not a global App effect.
 *
 * @dependencies none
 *
 * @notes
 * - Hidden tabs must not poll (visibilityState !== 'visible')
 * - HTTP 401 ends the poll and should be treated as signed-out
 * - HTTP 403 is authorization (company/permission), not session expiry
 * - On poll 401, clear local tokens before signOut so other tabs do not flash signed-in
 */

/**
 * Whether a transaction poll tick should run.
 *
 * @param {{ visibilityState?: string, signedOut?: boolean }} opts
 * @returns {boolean}
 */
export function shouldRunTransactionPoll({ visibilityState, signedOut } = {}) {
  if (signedOut) return false;
  return visibilityState === 'visible';
}

/**
 * Whether an HTTP status means the session ended for pollers (401 only).
 *
 * @param {number|undefined|null} status
 * @returns {boolean}
 */
export function isAuthFailureStatus(status) {
  return status === 401;
}

/**
 * Drop local auth tokens immediately on poll 401 (before signOut's logout round-trip).
 *
 * Same keys UserProfile logout-all clears so a still-logged-in UI cannot linger
 * on a dead session discovered by another tab.
 *
 * @param {{ sessionStorage?: Storage, apiClient?: { defaults?: { headers?: { common?: Record<string, string> } } } }} [deps]
 * @returns {void}
 */
export function clearLocalAuthTokensOnPollFailure(deps = {}) {
  const storage = deps.sessionStorage
    || (typeof sessionStorage !== 'undefined' ? sessionStorage : null);
  if (storage) {
    storage.removeItem('access_token');
    storage.removeItem('token_expires_at');
    storage.removeItem('wristband_auth_request_token');
  }

  const commonHeaders = deps.apiClient?.defaults?.headers?.common;
  if (commonHeaders) {
    delete commonHeaders.Authorization;
  }
}
